import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cn } from 'twyne-new.ui'

import { useLeftSidebar, useView } from '@/modules/cms/views/post/components/builder/context'

import Sidebar from '../Sidebar'
import { OVERLAY_TRANSITION, floatingPlacement } from '../sidebarLayout'

import Assets from './Assets'
import Blocks from './Blocks'
import Layers from './Layers'
import Pages from './Pages'

const OVERLAY_CLASSES = `absolute h-auto z-30 ${OVERLAY_TRANSITION}`

const VisibleTabContext = createContext({ tab: undefined, collapsed: true })

export default function LeftSidebar({ defaultValue, pages = <PagesSidebar />, layers = <LayersSidebar />, assets = <AssetsSidebar />, blocks = <BlocksSidebar /> }) {
  const { setValue } = useLeftSidebar()
  const visible = useVisibleTabState()

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue, setValue])

  return <VisibleTabContext.Provider value={visible}>
    {pages}
    {layers}
    {assets}
    {blocks}
  </VisibleTabContext.Provider>
}

function useVisibleTabState() {
  const { value: tabValue } = useLeftSidebar()
  const { value: view } = useView()

  const value = view === 'builder' ? tabValue : undefined
  const [visible, setVisible] = useState(() => ({ tab: value, collapsed: !value }))

  useLayoutEffect(() => {
    if (!value) {
      setVisible(current => ({ ...current, collapsed: true }))
      return
    }

    setVisible(current => ({ tab: value, collapsed: current.collapsed }))
  }, [value])

  const { tab, collapsed } = visible

  useEffect(() => {
    if (!value || !collapsed || tab !== value) {
      return
    }

    const frame = requestAnimationFrame(() => {
      setVisible(current => current.tab === value ? { ...current, collapsed: false } : current)
    })

    return () => cancelAnimationFrame(frame)
  }, [value, tab, collapsed])

  return visible
}

const useVisibleTab = () => useContext(VisibleTabContext)

function SidebarWrapper({ children, name, overlay = true, className, ...props }) {
  const { tab, collapsed } = useVisibleTab()

  return <Sidebar
    size='72'
    open={tab === name}
    className={cn(overlay && OVERLAY_CLASSES, overlay && collapsed && 'pointer-events-none', className)}
    style={overlay ? floatingPlacement('left', collapsed) : undefined}
    {...props}
  >
    {children}
  </Sidebar>
}

const PagesSidebar = () => <SidebarWrapper name='page'><Pages /></SidebarWrapper>
const LayersSidebar = () => <SidebarWrapper name='layers'><Layers /></SidebarWrapper>
const AssetsSidebar = () => {
  const ref = useRef()
  const { tab, collapsed } = useVisibleTab()

  return <div
    className={cn(OVERLAY_CLASSES, 'flex-row', collapsed && 'pointer-events-none', tab === 'assets' ? 'flex' : 'hidden')}
    style={floatingPlacement('left', collapsed)}
  >
    <SidebarWrapper name='assets' overlay={false}>
      <Assets floatbarRef={ref} />
    </SidebarWrapper>
    <div className='relative' ref={ref}></div>
  </div>
}
const BlocksSidebar = () => <SidebarWrapper name='blocks'><Blocks /></SidebarWrapper>
